import type { ApiErrorBody, ErrorCode } from '@giftgroup/shared';

/**
 * The typed error every failed request throws. Components switch on `code`, never on
 * the message, and error boundaries render `<ErrorState>` (doc 02).
 */
export class ApiError extends Error {
  readonly code: ErrorCode | 'NETWORK';
  readonly status: number;
  readonly details: unknown;

  constructor(code: ErrorCode | 'NETWORK', message: string, status: number, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  /** Field-level messages from a `VALIDATION` failure, for react-hook-form. */
  get fieldErrors(): Record<string, string[]> {
    const details = this.details as { fieldErrors?: Record<string, string[]> } | undefined;
    return details?.fieldErrors ?? {};
  }
}

type Options = { method?: string; body?: unknown; signal?: AbortSignal };

/**
 * Every call to our API goes through here: same-origin, cookies included, JSON in and
 * out, and errors turned into `ApiError`.
 */
export async function api<T>(path: string, options: Options = {}): Promise<T> {
  const { method = 'GET', body, signal } = options;

  // A file upload goes as multipart, and the browser must set the boundary itself —
  // so `FormData` is passed through with no content-type of ours (doc 08).
  const isForm = typeof FormData !== 'undefined' && body instanceof FormData;

  let response: Response;
  try {
    response = await fetch(`/api${path}`, {
      method,
      credentials: 'same-origin',
      headers: body === undefined || isForm ? {} : { 'content-type': 'application/json' },
      ...(body === undefined ? {} : { body: isForm ? (body as FormData) : JSON.stringify(body) }),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (signal?.aborted) throw cause;
    throw new ApiError('NETWORK', 'We couldn’t reach Gift Group. Check your connection.', 0);
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const payload: unknown = text ? JSON.parse(text) : null;

  if (!response.ok) {
    const error = (payload as ApiErrorBody | null)?.error;
    throw new ApiError(
      error?.code ?? 'INTERNAL',
      error?.message ?? 'Something went wrong on our end.',
      response.status,
      error?.details,
    );
  }

  return payload as T;
}

export const get = <T>(path: string, signal?: AbortSignal) =>
  api<T>(path, signal ? { signal } : {});
export const post = <T>(path: string, body?: unknown) => api<T>(path, { method: 'POST', body });
export const patch = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PATCH', body });
export const put = <T>(path: string, body?: unknown) => api<T>(path, { method: 'PUT', body });
export const del = <T>(path: string) => api<T>(path, { method: 'DELETE' });
